import { useState } from 'react';
import { ChevronLeft, ChevronRight, Quote, TrendingUp, Target } from 'lucide-react';
import { SpiderChart } from '../dashboard/SpiderChart';
import { ProgressBar } from '@/components/ui/atoms/ProgressBar';
import type { ProgressBarIntent } from '@/components/ui/atoms/ProgressBar';
import { Button } from '@/components/ui/atoms/Button';
import type { SalesEvaluation } from '../../services/salesDemoService';

interface SessionResultsProps {
  evaluation: SalesEvaluation;
}

type Slide = 'overview' | 'skills' | 'details';
const SLIDES: Slide[] = ['overview', 'skills', 'details'];

// Brand-only palette: single purple, opacity does the band differentiation so
// nothing reads as a traffic light. Same approach for both the number and the
// bar fill, no gradients (per the brand chart).
function scoreColor(score: number): string {
  if (score >= 8) return 'text-purple';
  if (score >= 5) return 'text-dark-invirtus';
  return 'text-dark-invirtus/55';
}

function scoreIntent(score: number): ProgressBarIntent {
  return score >= 5 ? 'primary' : 'secondary';
}

export function SessionResults({ evaluation }: SessionResultsProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const avgScore = Math.round(
    evaluation.dimensions.reduce((sum, d) => sum + d.score, 0) / evaluation.dimensions.length * 10,
  ) / 10;

  const spiderSkills = evaluation.dimensions.map(d => ({
    name: d.name,
    value: d.score,
  }));

  const goNext = () => setCurrentSlide(i => Math.min(i + 1, SLIDES.length - 1));
  const goPrev = () => setCurrentSlide(i => Math.max(i - 1, 0));

  return (
    <div className="bg-white rounded-2xl border border-gray-100 h-full flex flex-col overflow-hidden">
      {/* Header + dots */}
      <div className="px-5 pt-4 pb-2 flex items-center justify-between shrink-0">
        <h2 className="font-heading text-dark-invirtus text-base font-bold">
          Results
          <span className="font-body text-dark-invirtus/25 text-[10px] font-normal ml-2">
            scored out of 10
          </span>
        </h2>
        <div className="flex gap-1">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === currentSlide
                  ? 'bg-[var(--color-purple)] w-5'
                  : 'bg-gray-200 w-1.5 hover:bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Slide content */}
      <div className="flex-1 px-5 overflow-y-auto min-h-0">
        {/* Overview */}
        {SLIDES[currentSlide] === 'overview' && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple flex items-center justify-center shrink-0">
                <span className="text-lg font-bold text-white">{avgScore}</span>
              </div>
              <div>
                <p className="font-heading text-dark-invirtus text-sm font-bold">Overall Score</p>
                <p className="font-body text-dark-invirtus/30 text-[10px]">Average across 5 skills</p>
              </div>
            </div>

            <p className="font-body text-dark-invirtus/60 text-xs leading-relaxed">
              {evaluation.summary}
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <h3 className="font-heading text-dark-invirtus text-[10px] font-bold mb-1.5 flex items-center gap-1 uppercase tracking-wider">
                  <TrendingUp className="w-3 h-3 text-purple" />
                  Strengths
                </h3>
                <ul className="space-y-1">
                  {evaluation.strengths.map((s, i) => (
                    <li key={i} className="font-body text-[11px] text-dark-invirtus/50 leading-snug">
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-heading text-dark-invirtus text-[10px] font-bold mb-1.5 flex items-center gap-1 uppercase tracking-wider">
                  <Target className="w-3 h-3 text-dark-invirtus/45" />
                  Improve
                </h3>
                <ul className="space-y-1">
                  {evaluation.improvements.map((s, i) => (
                    <li key={i} className="font-body text-[11px] text-dark-invirtus/50 leading-snug">
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Skills radar */}
        {SLIDES[currentSlide] === 'skills' && (
          <div className="flex flex-col h-full gap-3">
            {/* Chart — bigger */}
            <div className="flex justify-center">
              <div className="w-full max-w-[320px]">
                <SpiderChart skills={spiderSkills} scale={10} />
              </div>
            </div>
            {/* Skill bars — clean, no grey bg */}
            <div className="w-full space-y-2 mt-auto pb-1">
              {evaluation.dimensions.map(d => (
                <div key={d.name} className="flex items-center gap-2.5">
                  <span className="font-body text-dark-invirtus/50 text-[11px] w-[110px] shrink-0">{d.name}</span>
                  <ProgressBar
                    value={d.score}
                    max={10}
                    size="md"
                    intent={scoreIntent(d.score)}
                    className="flex-1"
                    aria-label={d.name}
                  />
                  <span className={`font-heading text-xs font-bold w-5 text-right tabular-nums ${scoreColor(d.score)}`}>{d.score}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skill details */}
        {SLIDES[currentSlide] === 'details' && (
          <div className="space-y-3">
            {evaluation.dimensions.map((dim) => (
              <div key={dim.name} className="pb-3 border-b border-gray-100 last:border-0 last:pb-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-heading text-dark-invirtus text-xs font-bold">{dim.name}</h4>
                  <span className={`font-heading text-xs font-bold tabular-nums ${scoreColor(dim.score)}`}>
                    {dim.score}/10
                  </span>
                </div>
                <p className="font-body text-gray-700 text-[11px] mb-2 leading-snug">{dim.feedback}</p>
                <div className="flex gap-1.5 pl-2.5 border-l-2 border-gray-400">
                  <Quote className="w-2.5 h-2.5 text-gray-500 shrink-0 mt-0.5" />
                  <p className="font-body text-gray-600 text-[10px] italic leading-snug">
                    &ldquo;{dim.evidence}&rdquo;
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="px-5 py-3 flex items-center justify-between shrink-0 border-t border-gray-50">
        <Button
          type="icon"
          intent="muted"
          size="sm"
          icon={<ChevronLeft/>}
          onClick={goPrev}
          disabled={currentSlide === 0}
          aria-label="Previous slide"
        />
        <span className="font-body text-dark-invirtus/25 text-[10px] uppercase tracking-wider">
          {SLIDES[currentSlide]}
        </span>
        <Button
          type="icon"
          intent="muted"
          size="sm"
          icon={<ChevronRight/>}
          onClick={goNext}
          disabled={currentSlide === SLIDES.length - 1}
          aria-label="Next slide"
        />
      </div>
    </div>
  );
}
